import { useEffect, type ReactElement } from 'react';
import {
  Form,
  redirect,
  useActionData,
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation,
} from 'react-router';
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from '@marketplace/ui';
import { HANDSHAKE_TOKEN_PATTERN } from '@marketplace/contracts';
import { localeOf } from '../features/auth/actions.js';
import {
  forgetPendingToken,
  handshakeRefusalKey,
  pendingToken,
  rememberPendingToken,
  startLoginPath,
  tokenFromHash,
} from '../features/jobs/handshake.js';
import { ApiError } from '../shared/api.js';
import { queryKeys, routeContext } from '../shared/query.js';
import { loadSession } from '../shared/session.js';

/**
 * `W4-T09` §2.1, §4.4 — where the professional's QR lands: `/:lang/start#t=<token>`, opened by the
 * client's own camera in the client's own browser. **Their session is what redeems it**, so the
 * professional scanning their own code reaches this page signed in as themselves and is refused.
 *
 * - **The token rides in the fragment**, which a browser never sends to a server. It is read here and
 *   posted in a body, never put in a query string or a path. The router leaves the fragment off the
 *   loader's `request.url` (measured), so the page reads it from `useLocation()`.
 * - **Opening the page attests nothing.** The redeem happens only on Confirm, so a link preview, a
 *   prefetch or a stray tap releases no fee.
 * - **Signed out**, the token waits in `sessionStorage` while the client logs in, and the login page
 *   comes back here (`?next=`, a path without the token).
 *
 * The confirmation cannot name the professional: nothing maps a token to a person before it is
 * redeemed, and adding a read that did would be a token-lookup oracle. The job page's short-code form
 * names them, because there the job is known.
 */
interface StartData {
  locale: string;
  signedIn: boolean;
  /** A token a signed-out visit left waiting, for when the fragment is gone after the login. */
  pending: string | null;
}

type StartActionData = { error: string };

export async function loader({ params, context }: LoaderFunctionArgs): Promise<StartData> {
  const locale = localeOf(params);
  const session = await loadSession(context);
  return { locale, signedIn: session !== null, pending: pendingToken() };
}

export async function action({
  params,
  request,
  context,
}: ActionFunctionArgs): Promise<StartActionData | Response> {
  const locale = localeOf(params);
  const token = String((await request.formData()).get('token') ?? '');
  if (!HANDSHAKE_TOKEN_PATTERN.test(token)) return { error: 'handshake.error.notFound' };

  const { queryClient, api } = context.get(routeContext);
  try {
    const started = await api.redeemHandshake({ token });
    forgetPendingToken();
    // The job page reads the start: refetch it rather than serve the AWARDED job from the cache.
    await queryClient.invalidateQueries({
      queryKey: queryKeys.job(started.jobId),
      refetchType: 'all',
    });
    await queryClient.invalidateQueries({ queryKey: queryKeys.myJobs(), refetchType: 'all' });
    return redirect(`/${locale}/jobs/${started.jobId}`);
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      rememberPendingToken(token);
      throw redirect(startLoginPath(locale));
    }
    // A refusal is final for this token (not found, expired, redeemed, not theirs): it no longer
    // waits for anything. A failure that is not a refusal parks it, because the post left the
    // fragment behind and a retry needs it.
    if (error instanceof ApiError && error.status !== undefined && error.status < 500) {
      forgetPendingToken();
    } else {
      rememberPendingToken(token);
    }
    return { error: handshakeRefusalKey(error) };
  }
}

export function Component(): ReactElement {
  const { t } = useTranslation();
  const { locale, signedIn, pending } = useLoaderData<StartData>();
  const result = useActionData<StartActionData>();
  const navigation = useNavigation();
  const navigate = useNavigate();
  const token = tokenFromHash(useLocation().hash) ?? pending;

  // Signed out: park the token, then log in and come back. `replace`, so the entry holding the
  // fragment leaves the history as the visitor leaves the page.
  useEffect(() => {
    if (signedIn) return;
    if (token !== null) rememberPendingToken(token);
    void navigate(startLoginPath(locale), { replace: true });
  }, [signedIn, token, locale, navigate]);

  if (!signedIn) return <article data-testid="handshake-start-redirect" />;

  return (
    <article data-testid="handshake-start">
      <h1>{t('handshake.start.title')}</h1>
      {/* After a Confirm the location has no fragment, so a refusal is shown on its own; the form
          comes back only while a token is still waiting (a failure that was not a refusal). */}
      {result === undefined ? null : <Alert tone="error">{t(result.error)}</Alert>}
      {token !== null ? (
        <Form method="post">
          <input type="hidden" name="token" value={token} />
          <p className="mp-lead">{t('handshake.client.confirmAnyone')}</p>
          <Button type="submit" variant="primary" isDisabled={navigation.state !== 'idle'}>
            {t('handshake.client.confirmButton')}
          </Button>
        </Form>
      ) : result === undefined ? (
        <Alert tone="error">{t('handshake.error.notFound')}</Alert>
      ) : null}
    </article>
  );
}
